import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Report {
  readonly reportId: string;
  readonly reportCode: string;
  readonly reportName: string;
  readonly isPublished: boolean;
}

/** M26 — Report Builder (§16). Self-service report catalogue + creation. */
export function M26ReportBuilderPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<Report[]>([]);
  const [total, setTotal] = useState(0);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: Report[]; total: number }>('/v1/dwh/dashboards/reports', { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reloadKey]);

  async function create(): Promise<void> {
    if (!name.trim()) return;
    try { await apiRequest('/v1/dwh/dashboards/reports', { method: 'POST', body: { reportName: name, definitionJson: {} }, token }); setName(''); setReloadKey((k) => k + 1); }
    catch (e) { setError((e as Error).message); }
  }

  return (
    <main aria-label="report-builder">
      <h1>{t('m26.reportBuilder.title')}</h1>
      <label htmlFor="report-name">{t('m26.reportBuilder.reportNameLabel')}</label>
      <input id="report-name" value={name} onChange={(e) => setName(e.target.value)} />
      <button type="button" onClick={create}>{t('m26.reportBuilder.createReport')}</button>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m26.reportBuilder.loading')}</p> : (
        <>
          <p aria-label="report-count">{t('m26.reportBuilder.count', { count: total })}</p>
          <table aria-label="report-table">
            <thead><tr><th>{t('m26.reportBuilder.colCode')}</th><th>{t('m26.reportBuilder.colName')}</th><th>{t('m26.reportBuilder.colStatus')}</th></tr></thead>
            <tbody>{rows.map((r) => <tr key={r.reportId}><td>{r.reportCode}</td><td>{r.reportName}</td><td>{r.isPublished ? t('m26.reportBuilder.published') : t('m26.reportBuilder.draft')}</td></tr>)}</tbody>
          </table>
        </>
      )}
    </main>
  );
}
